// DEPLOY: /var/www/woody.wtf/frontend/src/chart/SetsMenu.js
// Chart 2 annotation sets menu (backend/drawings.py). Lists the user's own sets for the chart and the
// sets others made Public or shared with them. Eye = show / hide (several can show at once); tapping
// one of your own sets makes it the active set (new drawings go into it; the others show read-only).
// Own sets: rename, Private / Public / Share, share with usernames, delete. Others' sets: owner shown,
// "Edit copy" saves a copy as a new set of your own. Desktop popover, phone bottom sheet.
import React, { useState, useEffect, useRef } from 'react';
import { Layers, Eye, EyeOff } from 'lucide-react';

const S = {
  wrap:   { position: 'relative', flexShrink: 0 },
  btn:    { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', maxWidth: '180px' },
  btnName:{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  menu:   { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 120, width: '300px', maxHeight: '70vh', overflowY: 'auto', padding: '8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' },
  shade:  { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.35)' },
  sheet:  { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 301, maxHeight: '75vh', overflowY: 'auto', background: 'var(--surface)', borderRadius: '14px 14px 0 0', padding: '12px 16px calc(16px + env(safe-area-inset-bottom))', boxShadow: 'var(--shadow)' },
  head:   { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '6px 2px 4px' },
  row:    { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px', borderRadius: '4px' },
  icon:   { border: 'none', background: 'none', padding: '2px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' },
  name:   { flex: 1, minWidth: 0, border: 'none', background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tag:    { fontSize: '10px', fontWeight: 600, padding: '1px 5px', borderRadius: '3px', background: 'var(--bg)', color: 'var(--text-muted)', flexShrink: 0 },
  small:  { border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', color: 'var(--text)', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 },
  detail: { margin: '0 4px 8px 30px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' },
  input:  { flex: 1, minWidth: 0, padding: '5px 8px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', color: 'var(--text)' },
  seg:    { display: 'flex', border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' },
  segBtn: { flex: 1, border: 'none', borderRight: '1px solid var(--border)', padding: '4px 0', fontSize: '11px', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },
  on:     { background: 'var(--accent)', color: '#fff' },
  share:  { display: 'flex', alignItems: 'center', gap: '6px' },
  newBtn: { width: '100%', marginTop: '6px', padding: '8px', border: '1px dashed var(--border)', borderRadius: '4px', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
};

export default function SetsMenu({ sets, activeId, visible, mobile, onToggle, onActivate, onNew, onCopy, onRename,
                                   onPrivacy, onShare, onUnshare, onDelete }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);      // own set whose settings are expanded
  const [user, setUser] = useState('');
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open || mobile) return;
    const close = e => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open, mobile]);

  const own = sets.filter(s => s.own), others = sets.filter(s => !s.own);
  const active = sets.find(s => s.id === activeId);
  const eye = s => (
    <button style={S.icon} title={visible.has(s.id) ? 'Hide' : 'Show'} onClick={() => onToggle(s.id)}>
      {visible.has(s.id) ? <Eye size={15} /> : <EyeOff size={15} />}
    </button>
  );
  const body = (
    <>
      <div style={S.head}>My annotation sets</div>
      {!own.length && <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '4px' }}>None yet - your first drawing starts “My drawings”.</div>}
      {own.map(s => (
        <div key={s.id}>
          <div style={{ ...S.row, ...(s.id === activeId ? { background: 'var(--bg)' } : {}) }}>
            {eye(s)}
            <button style={{ ...S.name, fontWeight: s.id === activeId ? 700 : 400 }} title="Draw into this set" onClick={() => onActivate(s.id)}>
              {s.id === activeId ? '● ' : ''}{s.name}
            </button>
            {s.privacy !== 'Private' && <span style={S.tag}>{s.privacy}</span>}
            <button style={S.icon} title="Settings" onClick={() => setDetail(d => (d === s.id ? null : s.id))}>⋯</button>
          </div>
          {detail === s.id && (
            <div style={S.detail}>
              <div style={S.share}>
                <button style={S.small} onClick={() => { const n = window.prompt('Rename set', s.name); if (n && n.trim()) onRename(s, n.trim()); }}>Rename</button>
                <button style={{ ...S.small, color: 'var(--error)' }}
                        onClick={() => { if (window.confirm(`Delete “${s.name}” and all its drawings?`)) onDelete(s); }}>Delete</button>
              </div>
              <div style={S.seg}>
                {['Private', 'Public', 'Share'].map((p, i) => (
                  <button key={p} style={{ ...S.segBtn, ...(i === 2 ? { borderRight: 'none' } : {}), ...(s.privacy === p ? S.on : {}) }}
                          onClick={() => onPrivacy(s, p)}>{p}</button>
                ))}
              </div>
              {s.privacy === 'Public' && <span style={{ color: 'var(--text-muted)' }}>Every user sees this set (read-only) on this chart.</span>}
              {s.privacy === 'Share' && <>
                <form style={S.share} onSubmit={e => { e.preventDefault(); if (user.trim()) { onShare(s, user.trim()); setUser(''); } }}>
                  <input style={S.input} value={user} onChange={e => setUser(e.target.value)} placeholder="Username" autoCapitalize="none" />
                  <button type="submit" style={S.small}>Share</button>
                </form>
                {(s.shares || []).map(u => (
                  <div key={u} style={S.share}>
                    <span style={{ flex: 1 }}>{u}</span>
                    <button style={S.small} onClick={() => onUnshare(s, u)}>Remove</button>
                  </div>
                ))}
              </>}
            </div>
          )}
        </div>
      ))}
      <button style={S.newBtn} onClick={() => { const n = window.prompt('Name for the new set'); if (n && n.trim()) onNew(n.trim()); }}>+ New set</button>
      {others.length > 0 && <>
        <div style={{ ...S.head, marginTop: '12px' }}>Shared with me</div>
        {others.map(s => (
          <div key={s.id} style={S.row}>
            {eye(s)}
            <span style={{ ...S.name, cursor: 'default' }} title={`${s.name} by ${s.owner} (read-only)`}>
              {s.name} <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>· {s.owner}</span>
            </span>
            <button style={S.small} title="Save a copy as a set of your own to edit" onClick={() => onCopy(s)}>Edit copy</button>
          </div>
        ))}
      </>}
    </>
  );

  return (
    <div ref={boxRef} style={S.wrap}>
      <button style={S.btn} onClick={() => setOpen(o => !o)} title="Annotation sets">
        <Layers size={14} />
        {!mobile && <span style={S.btnName}>{active ? active.name : 'Drawings'}</span>}
        {others.length > 0 && <span style={S.tag}>{others.length}</span>}
      </button>
      {open && (mobile
        ? <><div style={S.shade} onClick={() => setOpen(false)} /><div style={S.sheet}>{body}</div></>
        : <div style={S.menu}>{body}</div>)}
    </div>
  );
}
